import { useState, useRef, useEffect } from 'react'
import { Loader2, CheckCircle2, AlertTriangle, ChevronDown, ChevronUp } from 'lucide-react'
import { useBackgroundTasksStore, type BackgroundTask } from '@/stores/backgroundTasks.ts'
import { cn } from '@/lib/utils.ts'
import { apiClient } from '@/lib/api.ts'

/**
 * Task status endpoint config: maps task IDs to their backend polling endpoints
 * and completion-detection logic.
 */
const TASK_STATUS_ENDPOINTS: Record<string, {
  url: string
  /** Extract status from response. Return { done, detail, log? } */
  parse: (data: any) => { done: boolean; detail: string; status: 'running' | 'done' | 'error'; log?: BackgroundTask['log'] } | null
}> = {
  discovery: {
    url: '/acquisition/discovery/status',
    parse: (data) => {
      if (!data) return null
      if (data.done) {
        return {
          done: true,
          detail: data.message || 'Firmensuche abgeschlossen',
          status: data.message?.includes('Fehler') ? 'error' : 'done',
          log: data.log,
        }
      }
      if (data.log?.length) {
        const last = data.log[data.log.length - 1]
        return {
          done: false,
          detail: last ? `${last.step}: ${last.detail}` : 'Firmensuche läuft...',
          status: 'running',
          log: data.log,
        }
      }
      return null // No info available (Redis empty or down)
    },
  },
  contacts: {
    url: '/acquisition/prospects/enrich-status',
    parse: (data) => {
      if (!data?.message) return null
      const done = data.message.startsWith('Fertig:')
      return { done, detail: data.message, status: done ? 'done' : 'running' }
    },
  },
  profiling: {
    url: '/acquisition/prospects/profile-status',
    parse: (data) => {
      if (!data?.message) return null
      const done = data.message.startsWith('Profiling fertig')
      return { done, detail: data.message, status: done ? 'done' : 'running' }
    },
  },
}

/** Max age in ms before a persisted running task is considered stale (15 min) */
const STALE_TASK_AGE = 15 * 60 * 1000

/**
 * On mount, recover persisted running tasks by validating against backend
 * and resuming polling. This runs in GlobalStatusBar which is always mounted.
 */
function useRecoverPersistedTasks() {
  const recovered = useBackgroundTasksStore((s) => s._recovered)
  const upsertTask = useBackgroundTasksStore((s) => s.upsertTask)
  const removeTask = useBackgroundTasksStore((s) => s.removeTask)
  const markRecovered = useBackgroundTasksStore((s) => s.markRecovered)
  const pollRefs = useRef<Record<string, ReturnType<typeof setInterval>>>({})

  useEffect(() => {
    if (recovered) return

    // Small delay to let zustand persist rehydrate
    const timeout = setTimeout(() => {
      const tasks = useBackgroundTasksStore.getState().tasks
      const runningTasks = Object.values(tasks).filter((t) => t.status === 'running')
      markRecovered()

      if (runningTasks.length === 0) return

      for (const task of runningTasks) {
        const endpoint = TASK_STATUS_ENDPOINTS[task.id]
        if (!endpoint) {
          // No status endpoint for this task — check if stale
          if (Date.now() - task.updatedAt > STALE_TASK_AGE) {
            removeTask(task.id)
          }
          continue
        }

        // Validate against backend
        apiClient.get(endpoint.url).then(({ data }) => {
          const parsed = endpoint.parse(data)

          if (parsed) {
            // Backend has status — update task
            upsertTask({
              id: task.id,
              label: task.label,
              detail: parsed.detail,
              status: parsed.status,
              log: parsed.log,
              updatedAt: Date.now(),
            })

            // If still running, start polling
            if (!parsed.done) {
              startPolling(task.id, task.label, endpoint)
            }
          } else {
            // Backend has no status — could be Redis down or task finished long ago
            // If task was persisted recently (< 15min), keep it and try polling
            if (Date.now() - task.updatedAt < STALE_TASK_AGE) {
              startPolling(task.id, task.label, endpoint)
            } else {
              removeTask(task.id)
            }
          }
        }).catch(() => {
          // Network error — keep the task, it might still be running
          if (Date.now() - task.updatedAt < STALE_TASK_AGE) {
            startPolling(task.id, task.label, endpoint)
          } else {
            removeTask(task.id)
          }
        })
      }
    }, 100)

    return () => clearTimeout(timeout)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recovered])

  // Cleanup all polls on unmount
  useEffect(() => {
    return () => {
      for (const id of Object.keys(pollRefs.current)) {
        clearInterval(pollRefs.current[id])
      }
    }
  }, [])

  function startPolling(
    taskId: string,
    taskLabel: string,
    endpoint: (typeof TASK_STATUS_ENDPOINTS)[string],
  ) {
    // Don't start duplicate polls
    if (pollRefs.current[taskId]) return

    let emptyCount = 0
    const maxEmptyPolls = 10 // Give up after 10 empty polls (30s)

    pollRefs.current[taskId] = setInterval(() => {
      apiClient.get(endpoint.url).then(({ data }) => {
        const parsed = endpoint.parse(data)

        if (parsed) {
          emptyCount = 0
          upsertTask({
            id: taskId,
            label: taskLabel,
            detail: parsed.detail,
            status: parsed.status,
            log: parsed.log,
            updatedAt: Date.now(),
          })

          if (parsed.done) {
            clearInterval(pollRefs.current[taskId])
            delete pollRefs.current[taskId]
          }
        } else {
          emptyCount++
          if (emptyCount >= maxEmptyPolls) {
            // No status after many polls — task likely finished or crashed
            removeTask(taskId)
            clearInterval(pollRefs.current[taskId])
            delete pollRefs.current[taskId]
          }
        }
      }).catch(() => {
        emptyCount++
        if (emptyCount >= maxEmptyPolls) {
          removeTask(taskId)
          clearInterval(pollRefs.current[taskId])
          delete pollRefs.current[taskId]
        }
      })
    }, 3000)

    // Safety: stop polling after 10 minutes
    setTimeout(() => {
      if (pollRefs.current[taskId]) {
        clearInterval(pollRefs.current[taskId])
        delete pollRefs.current[taskId]
      }
    }, 600000)
  }
}

export function GlobalStatusBar() {
  useRecoverPersistedTasks()

  const tasks = useBackgroundTasksStore((s) => s.tasks)
  const removeTask = useBackgroundTasksStore((s) => s.removeTask)
  const allTasks = Object.values(tasks).sort((a, b) => b.updatedAt - a.updatedAt)
  const runningTasks = allTasks.filter((t) => t.status === 'running')
  const [expanded, setExpanded] = useState(false)
  const [expandedLogs, setExpandedLogs] = useState<Record<string, boolean>>({})
  const panelRef = useRef<HTMLDivElement>(null)

  // Auto-clean completed/error tasks after 30s
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now()
      for (const task of allTasks) {
        if (task.status !== 'running' && now - task.updatedAt > 30000) {
          removeTask(task.id)
        }
      }
    }, 5000)
    return () => clearInterval(interval)
  }, [allTasks, removeTask])

  // Close panel on outside click
  useEffect(() => {
    if (!expanded) return
    const handler = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setExpanded(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [expanded])

  if (allTasks.length === 0) return null

  const latestRunning = runningTasks[0]
  const hasErrors = allTasks.some((t) => t.status === 'error')

  return (
    <div ref={panelRef} className="relative">
      {/* Compact indicator */}
      <button
        onClick={() => setExpanded(!expanded)}
        className={cn(
          'flex items-center gap-1.5 px-2 py-0.5 rounded text-[11px] font-medium transition-colors',
          runningTasks.length > 0
            ? 'bg-amber-500/20 text-amber-900 hover:bg-amber-500/30'
            : hasErrors
              ? 'bg-red-500/20 text-red-900 hover:bg-red-500/30'
              : 'bg-green-500/20 text-green-900 hover:bg-green-500/30'
        )}
      >
        {runningTasks.length > 0 ? (
          <Loader2 className="w-3 h-3 animate-spin" />
        ) : hasErrors ? (
          <AlertTriangle className="w-3 h-3" />
        ) : (
          <CheckCircle2 className="w-3 h-3" />
        )}
        <span className="max-w-[300px] truncate">
          {runningTasks.length > 0
            ? latestRunning!.detail || latestRunning!.label
            : hasErrors
              ? `${allTasks.filter((t) => t.status === 'error').length} Fehler`
              : 'Abgeschlossen'}
        </span>
        {allTasks.length > 1 && (
          <span className="opacity-60">+{allTasks.length - 1}</span>
        )}
        {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
      </button>

      {/* Expanded dropdown panel */}
      {expanded && (
        <div className="absolute top-full left-0 mt-1 w-[480px] bg-white rounded-lg shadow-xl border border-gray-200 z-[60] max-h-[70vh] overflow-y-auto">
          <div className="px-3 py-2 border-b border-gray-100 flex items-center justify-between">
            <span className="text-xs font-semibold text-gray-700">Aktive Aktionen</span>
            {runningTasks.length > 0 && (
              <span className="text-[10px] text-amber-600 flex items-center gap-1">
                <Loader2 className="w-2.5 h-2.5 animate-spin" />
                {runningTasks.length} laufend
              </span>
            )}
          </div>
          <div className="divide-y divide-gray-50">
            {allTasks.map((task) => (
              <TaskEntry
                key={task.id}
                task={task}
                logExpanded={expandedLogs[task.id] ?? false}
                onToggleLog={() => setExpandedLogs((prev) => ({ ...prev, [task.id]: !prev[task.id] }))}
                onDismiss={() => removeTask(task.id)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

function TaskEntry({
  task,
  logExpanded,
  onToggleLog,
  onDismiss,
}: {
  task: BackgroundTask
  logExpanded: boolean
  onToggleLog: () => void
  onDismiss: () => void
}) {
  return (
    <div className="px-3 py-2">
      <div className="flex items-center gap-2">
        {task.status === 'running' ? (
          <Loader2 className="w-3.5 h-3.5 animate-spin text-amber-500 flex-shrink-0" />
        ) : task.status === 'error' ? (
          <AlertTriangle className="w-3.5 h-3.5 text-red-500 flex-shrink-0" />
        ) : (
          <CheckCircle2 className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-gray-800">{task.label}</span>
            {task.status !== 'running' && (
              <button
                onClick={onDismiss}
                className="text-[10px] text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            )}
          </div>
          <p className={cn('text-[11px] truncate',
            task.status === 'error' ? 'text-red-600' :
            task.status === 'done' ? 'text-green-600' : 'text-gray-500'
          )}>
            {task.detail}
          </p>
        </div>
        {task.log && task.log.length > 0 && (
          <button
            onClick={onToggleLog}
            className="text-[10px] text-gray-400 hover:text-gray-600 flex-shrink-0"
          >
            {logExpanded ? 'Log ▲' : 'Log ▼'}
          </button>
        )}
      </div>
      {logExpanded && task.log && task.log.length > 0 && (
        <div className="mt-1.5 ml-5 bg-gray-50 rounded p-2 space-y-0.5 max-h-40 overflow-y-auto">
          {task.log.map((entry, i) => (
            <div key={i} className="flex items-start gap-1.5 text-[10px]">
              <span className={cn('w-1.5 h-1.5 rounded-full mt-1 flex-shrink-0',
                entry.status === 'success' ? 'bg-green-500' :
                entry.status === 'error' ? 'bg-red-500' :
                entry.status === 'warning' ? 'bg-amber-500' : 'bg-gray-300'
              )} />
              <span className="text-gray-400 font-medium min-w-[70px]">{entry.step}</span>
              <span className="text-gray-600">{entry.detail}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
